import { BaseSchemes, NodeEditor, Root, Scope } from 'rete';
import { AreaPlugin, BaseArea, Drag } from 'rete-area-plugin';
import { MarqueeOptions, Preset, Produces, SelectableNodes } from './interfaces/marquee-plugin.interface';

export class MarqueePlugin<Schemes extends BaseSchemes> extends Scope<Produces, [BaseArea<Schemes>, Root<Schemes>]> {
  static pluginName = 'marquee';

  protected opts: Required<MarqueeOptions>;
  protected marqueeEl!: HTMLDivElement;
  protected isDragging = false;
  protected dragStart = { x: 0, y: 0 };
  protected lastRect = { left: 0, top: 0, width: 0, height: 0 };
  protected selected = new Set<number | string>();
  protected listeners: Array<{ target: EventTarget; type: string; fn: EventListener }> = [];
  protected editor!: NodeEditor<Schemes>;
  protected area!: AreaPlugin<Schemes, BaseArea<Schemes>>;
  protected presets: Preset<Schemes>[] = [];
  protected drag: Drag;
  protected selectableNodes: SelectableNodes;

  constructor(options: MarqueeOptions) {
    super(MarqueePlugin.pluginName);

    if (!options?.container) {
      throw new Error('MarqueePlugin: container is required');
    }

    this.opts = {
      nodeSelector: '.neqto-node',
      nodeIdAttr: 'data-node-id',
      selectedClass: 'selected',
      marqueeClass: 'flow-editor-marquee',
      multiSelectWithShift: true,
      clearOnStart: true,
      enabled: () => {
        return true;
      },
      deleteConfirm: () => {},
      ...options,
    };

    this.selectableNodes = this.opts.selectableNodes;
    this.drag = new Drag();

    this.#createOverlay();
  }

  override setParent(scope: Scope<BaseArea<Schemes>, [Root<Schemes>]>): void {
    super.setParent(scope);

    this.area = this.parentScope<AreaPlugin<Schemes, BaseArea<Schemes>>>(AreaPlugin);
    this.editor = this.area.parentScope<NodeEditor<Schemes>>(NodeEditor);

    this.initMarqueePlugin();

    this.presets.forEach((preset) => {
      preset.connect(this);
    });

    this.editor.addPipe((context) => {
      if (context.type === 'cleared') {
        this.destroy();
      }

      return context;
    });
  }

  addPreset(preset: Preset): void {
    this.presets.push(preset);

    if (this.area && this.editor) {
      preset.connect(this);
    }
  }

  initMarqueePlugin(): void {
    this.#addListener(this.opts.container, 'mousedown', this.onMouseDown.bind(this));
    this.#addListener(document, 'mousemove', this.onMouseMove.bind(this));
    this.#addListener(document, 'mouseup', this.onMouseUp.bind(this));
    this.#addListener(document, 'keydown', this.onKeyDown.bind(this));
  }

  destroy(): void {
    try {
      this.marqueeEl.remove();
    } catch {
      console.warn('MarqueePlugin: failed to remove marquee element');
    }

    this.listeners.forEach((listener) => {
      listener.target.removeEventListener(listener.type, listener.fn);
    });

    this.listeners = [];
    this.selected.clear();
  }

  async select(ids: Array<number | string>): Promise<void> {
    for (const id of ids) {
      await this.applySelection(id, true);
    }

    await this.emitSelectionChange();
  }

  async clear(): Promise<void> {
    for (const id of Array.from(this.selected)) {
      await this.applySelection(id, false);
    }

    this.selected.clear();
    await this.emitSelectionChange();
  }

  getSelected(): Array<number | string> {
    return Array.from(this.selected);
  }

  protected onMouseDown(event: MouseEvent): void {
    const minimap = (event.target as HTMLElement).closest('rete-minimap');

    if (!this.opts.enabled() || minimap) {
      return;
    }

    this.area.area.setDragHandler(null); // disable area drag

    if (event.button !== 0 || this.#isEventInsideNode(event)) {
      return;
    }

    if (this.opts.clearOnStart && !this.#multiKeyPressed(event)) {
      this.clear();
    }

    const rect = this.opts.container.getBoundingClientRect();

    this.dragStart = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    this.lastRect = { left: this.dragStart.x, top: this.dragStart.y, width: 0, height: 0 };
    this.isDragging = true;

    Object.assign(this.marqueeEl.style, {
      display: 'block',
      left: `${this.lastRect.left}px`,
      top: `${this.lastRect.top}px`,
      width: `0px`,
      height: `0px`,
    });
  }

  protected onMouseMove(event: MouseEvent): void {
    if (!this.opts.enabled() || !this.isDragging) {
      return;
    }

    const rect = this.opts.container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const left = Math.min(this.dragStart.x, x);
    const top = Math.min(this.dragStart.y, y);
    const width = Math.abs(x - this.dragStart.x);
    const height = Math.abs(y - this.dragStart.y);

    this.lastRect = { left, top, width, height };

    Object.assign(this.marqueeEl.style, {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    });
  }

  protected async onMouseUp(event: MouseEvent): Promise<void> {
    if (!this.opts.enabled() || !this.isDragging) {
      return;
    }

    this.isDragging = false;
    this.marqueeEl.style.display = 'none';

    const hits = this.#computeNodesInRect(this.lastRect);
    const modifier = this.#multiKeyPressed(event);

    // If clearOnStart is false and no modifier: clear selection first
    if (!modifier && !this.opts.clearOnStart) {
      this.clear();
    }

    // Add a hits to selection
    for (const id of hits) {
      if (!this.selected.has(id)) {
        await this.applySelection(id, true);
      }
    }

    await this.emitSelectionChange();
    this.area.area.setDragHandler(this.drag);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (!this.opts.enabled()) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        this.clear();
        break;
      case 'Backspace':
      case 'Delete':
        // delete selected nodes
        if (this.selected.size > 0) {
          this.opts.deleteConfirm(this.clear.bind(this));
        }
        break;
      default:
        break;
    }
  }

  protected async applySelection(id: number | string, select: boolean): Promise<void> {
    const node = this.#findNodeElementById(id);
    const [, nodeId] = String(id).split('node');

    if (!node) {
      return;
    }

    const castNodeId = Math.sign(Number(nodeId)) !== -1 ? +nodeId : nodeId;

    if (select) {
      this.selected.add(id);
      await this.selectableNodes.select(castNodeId, true);
    } else {
      this.selected.delete(id);
      await this.selectableNodes.unselect(castNodeId);
    }
  }

  protected async emitSelectionChange(): Promise<void> {
    await this.emit({
      type: 'selectionchanged',
      data: this.getSelected(),
    });
  }

  #computeNodesInRect(rect: { left: number; top: number; width: number; height: number }): Set<number | string> {
    const containerRect = this.opts.container.getBoundingClientRect();
    const selection = {
      left: containerRect.left + rect.left,
      top: containerRect.top + rect.top,
      right: containerRect.left + rect.left + rect.width,
      bottom: containerRect.top + rect.top + rect.height,
    };

    const nodes = Array.from(this.opts.container.querySelectorAll<HTMLElement>(this.opts.nodeSelector));
    const hits = new Set<number | string>();

    for (const node of nodes) {
      const nodeBoundingClientRect = node.getBoundingClientRect();
      const intersecting = !(
        nodeBoundingClientRect.left > selection.right ||
        nodeBoundingClientRect.right < selection.left ||
        nodeBoundingClientRect.top > selection.bottom ||
        nodeBoundingClientRect.bottom < selection.top
      );

      if (intersecting) {
        const nodeIdAttr = node.getAttribute(this.opts.nodeIdAttr) ?? (node.dataset as any)?.nodeId;

        if (nodeIdAttr == null) {
          console.warn(`MarqueePlugin: node element missing ${this.opts.nodeIdAttr}`);
          continue;
        }

        const id = isNaN(Number(nodeIdAttr)) ? nodeIdAttr : Number(nodeIdAttr);

        hits.add(id);
      }
    }

    return hits;
  }

  #multiKeyPressed(event: MouseEvent | KeyboardEvent): boolean {
    return this.opts.multiSelectWithShift
      ? (event as MouseEvent).shiftKey
      : (event as MouseEvent).ctrlKey || (event as MouseEvent).metaKey;
  }

  #isEventInsideNode(event: MouseEvent): boolean {
    let element = event.target as HTMLElement | null;
    const container = this.opts.container;

    while (element && element !== container) {
      if (element.matches && element.matches(this.opts.nodeSelector)) {
        return true;
      }

      element = element.parentElement;
    }

    return false;
  }

  #findNodeElementById(id: number | string): HTMLElement | null {
    const sel = `${this.opts.nodeSelector}[${this.opts.nodeIdAttr}="${id}"], ${this.opts.nodeSelector}[data-node-id="${id}"]`;

    return this.opts.container.querySelector<HTMLElement>(sel);
  }

  #addListener(target: EventTarget, type: string, fn: EventListener): void {
    target.addEventListener(type, fn);
    this.listeners.push({ target, type, fn });
  }

  #createOverlay(): void {
    const marqueeElement = document.createElement('div');
    const containerComputedStyle = getComputedStyle(this.opts.container);

    marqueeElement.className = this.opts.marqueeClass;

    if (containerComputedStyle.position === 'static') {
      this.opts.container.style.position = 'relative';
    }

    this.opts.container.appendChild(marqueeElement);
    this.marqueeEl = marqueeElement;
  }
}
